import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { saveUnlock } from "@/lib/useEbookAccess";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TARGET = { ebook_atlas: "/ebook", ebook_cookbook: "/ricettario", ebook_bundle: "/ebook" };

export default function PaymentSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const product = params.get("product") || "ebook_atlas";
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return undefined; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid" && data.token) {
          (data.licenses || [{ product: data.product, token: data.token }]).forEach((l) => saveUnlock(l.product, l.token));
          trackEvent("purchase", { product: data.product, value: data.product === "ebook_bundle" ? 1.49 : 0.99, currency: "EUR" });
          setState("paid");
          toast.success(t("paywall_unlocked"));
          timer = setTimeout(() => navigate(TARGET[data.product] || "/", { replace: true }), 1800);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (++tries.current < 10) timer = setTimeout(poll, 2000); else setState("failed");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, navigate, t]);

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center flex flex-col items-center gap-4" data-testid="payment-success">
      {state === "checking" && <><Loader2 className="w-10 h-10 text-ochre animate-spin" /><p className="text-base text-ink" data-testid="payment-checking">{t("payment_checking")}</p></>}
      {state === "paid" && <><CheckCircle2 className="w-12 h-12 text-oasis" /><h1 className="font-display font-bold text-3xl text-ink" data-testid="payment-paid">{t("paywall_unlocked")}</h1><Link to={TARGET[product] || "/"} className="text-sm text-ochre hover:underline" data-testid="payment-open-ebook">{t("payment_open_ebook")} →</Link></>}
      {state === "failed" && <><XCircle className="w-12 h-12 text-destructive" /><p className="text-base text-ink" data-testid="payment-failed">{t("payment_failed")}</p><Link to={TARGET[product] || "/"} className="inline-flex items-center gap-1.5 text-sm text-ochre hover:underline"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("back")}</Link></>}
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const product = params.get("product") || "ebook_atlas";
  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center flex flex-col items-center gap-4" data-testid="payment-cancel">
      <XCircle className="w-12 h-12 text-muted-foreground" />
      <p className="text-base text-ink">{t("payment_cancel")}</p>
      <Link to={TARGET[product] || "/"} className="inline-flex items-center gap-1.5 text-sm text-ochre hover:underline" data-testid="payment-cancel-back"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("back")}</Link>
    </div>
  );
}
